import { api } from '@agency/shared';
import { Chip, DataTable, PageHeader, type DataTableColumn } from '@agency/ui';
import { useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo } from 'react';
import { ErrorCallout, Page, Stamp, TableCard } from '../../app/widgets.js';
import { TEMPLATE_TYPE_LABEL } from '../../lib/labels.js';
import { useApi, usePaged } from '../../lib/useApi.js';

interface TypeRow {
  type: api.TemplateType;
  count: number;
  active: number;
  drafts: number;
  reviewed: number;
  lastPublished: string | null;
}

/** Content overview: one row per content type with active versions, draft counts and last publication. */
export function ContentPage() {
  const navigate = useNavigate();
  const templates = usePaged('admin.templates.list', { input: { limit: 200 } });
  // The overview counts every item, so it walks the whole list instead of showing one page's size.
  useEffect(() => {
    if (templates.hasMore && !templates.loadingMore && !templates.loading) void templates.loadMore();
  }, [templates.hasMore, templates.loadingMore, templates.loading, templates.loadMore]);
  const releases = useApi('admin.content.releases.list', { input: { limit: 1 } });
  const rows = useMemo<TypeRow[]>(() => {
    const all = templates.items;
    return api.TEMPLATE_TYPES.map((type) => {
      const list = all.filter((t) => t.type === type);
      return {
        type,
        count: list.length,
        active: list.filter((t) => t.activeVersionId !== null).length,
        drafts: list.filter((t) => t.latestState === 'draft' || t.latestState === 'validated' || t.latestState === 'previewed').length,
        reviewed: list.filter((t) => t.latestState === 'reviewed').length,
        lastPublished: list.reduce<string | null>((acc, t) => (t.activeVersionId && (!acc || t.updatedAt > acc) ? t.updatedAt : acc), null),
      };
    });
  }, [templates.items]);
  const columns = useMemo<DataTableColumn<TypeRow>[]>(
    () => [
      { id: 'type', header: 'Content type', cell: ({ row }) => <span className="text-ink">{TEMPLATE_TYPE_LABEL[row.original.type]}</span>, size: 220 },
      { id: 'key', header: 'Key', cell: ({ row }) => <span className="font-mono text-xs-plus text-mute">{row.original.type}</span>, size: 180 },
      { id: 'count', header: 'Items', cell: ({ row }) => <span className="block text-right font-mono tabular-nums">{row.original.count}</span>, size: 80 },
      { id: 'active', header: 'Active', cell: ({ row }) => <span className="block text-right font-mono tabular-nums">{row.original.active}</span>, size: 80 },
      { id: 'drafts', header: 'Drafts', cell: ({ row }) => <span className="block text-right font-mono tabular-nums">{row.original.drafts}</span>, size: 80 },
      { id: 'reviewed', header: 'Awaiting publish', cell: ({ row }) => (row.original.reviewed > 0 ? <Chip tone="warn">{row.original.reviewed} reviewed</Chip> : <span className="text-mute">--</span>), size: 140 },
      { id: 'last', header: 'Last change', cell: ({ row }) => <Stamp value={row.original.lastPublished} relative />, size: 120 },
    ],
    [],
  );
  const latest = releases.data?.items[0];
  return (
    <Page>
      <PageHeader eyebrow="Templates and rules" eyebrowPath="Content" title="Content" subtitle={templates.loading || templates.hasMore ? 'Loading content items.' : `${templates.items.length} items · ${rows.filter((r) => r.count > 0).length} of ${api.TEMPLATE_TYPES.length} types populated · active bundle ${latest ? `${latest.manifest.version} (${latest.manifest.items.length} items) · ${latest.manifest.channel}` : '--'}`} />
      <ErrorCallout error={templates.error} onRetry={() => void templates.reload()} className="mt-6" />
      <div className="mt-6">
        <TableCard title="Content types" subtitle="Draft → validated → previewed → reviewed → published → active">
          <DataTable<TypeRow> aria-label="Content types" columns={columns} rows={rows} rowKey={(r) => r.type} height={rows.length * 34 + 40} state={templates.loading ? 'loading' : 'complete'} noun="types" onSelect={(type) => void navigate({ to: '/templates', search: { type } })} onActivate={(type) => void navigate({ to: '/templates', search: { type } })} />
        </TableCard>
      </div>
    </Page>
  );
}
